/**
 * Modal "Dejé el barrio" — Sprint 18 P3 B3.
 *
 * El vecino cierra su propia tenencia (A1 del flujo de salida).
 * Solo aplica a tipos `propietario_residente`, `arrendatario`,
 * `comodatario`. El propietario_no_residente y `otro` salen por
 * comisión.
 *
 * Side-effects backend (D-P3-1):
 * - Inactiva todos los residentes de la tenencia.
 * - Inactiva todos los vehículos del lote.
 * - Recursos del lote pasan a "bolsa" (próximo titular los hereda).
 * - Solicitudes de recurso pendientes se cancelan.
 * - Notifica a la comisión para que baje celulares de citofonía y
 *   accesos de wifi del residente saliente.
 * - Si la persona queda sin otras tenencias activas, queda como
 *   ex-vecino (Persona.activo=False).
 */
import { useState, type FormEvent } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { InputFecha } from '../ui/InputFecha';
import { ApiError } from '../../lib/api/client';
import {
  cambiarMiTenenciaANoResidente,
  cerrarMiTenencia,
} from '../../lib/api/tenencias';
import type { CerrarTenenciaResponse } from '../../lib/api/types';
import { hoy } from '../../lib/tiempo';

interface MotivoOption {
  value: string;
  label: string;
}

const MOTIVOS_POR_TIPO: Record<string, MotivoOption[]> = {
  propietario_residente: [
    { value: 'venta', label: 'Vendí la propiedad' },
    {
      value: 'salida_propietario_residente',
      label: 'Me mudo pero sigo siendo dueño (la arriendo)',
    },
    { value: 'otro', label: 'Otro motivo' },
  ],
  arrendatario: [
    { value: 'renuncia_arrendatario', label: 'Termina mi arriendo' },
    { value: 'otro', label: 'Otro motivo' },
  ],
  comodatario: [
    { value: 'otro', label: 'Salida del comodato' },
  ],
};

interface ModalDejeBarrioProps {
  /** Tenencia activa del vecino. */
  tenenciaId: string;
  tipoTenencia: string;
  loteIdentificador: string;
  onCerrar: () => void;
  /** Callback tras cierre exitoso. Recibe los counters para feedback. */
  onCerrado: (resultado: CerrarTenenciaResponse) => void;
}

export function ModalDejeBarrio({
  tenenciaId,
  tipoTenencia,
  loteIdentificador,
  onCerrar,
  onCerrado,
}: ModalDejeBarrioProps) {
  const motivos = MOTIVOS_POR_TIPO[tipoTenencia] ?? [
    { value: 'otro', label: 'Otro motivo' },
  ];
  const fechaHoy = hoy();
  const [fecha, setFecha] = useState(fechaHoy);
  const [motivo, setMotivo] = useState(motivos[0]?.value ?? 'otro');
  const [notas, setNotas] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Confirmación doble: primero el form, luego "¿Estás seguro?"
  const [confirmando, setConfirmando] = useState(false);

  // P24: este motivo NO cierra la tenencia — la convierte a no residente.
  const esTransicion = motivo === 'salida_propietario_residente';

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!confirmando) {
      setConfirmando(true);
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // Sprint 18 P24: "me mudo pero sigo siendo dueño" NO cierra la
      // tenencia — la convierte a propietario_no_residente.
      const resultado = esTransicion
        ? await cambiarMiTenenciaANoResidente(tenenciaId)
        : await cerrarMiTenencia(tenenciaId, {
            fecha_cierre: fecha,
            motivo_cierre: motivo,
            notas_cierre: notas.trim() || null,
          });
      onCerrado(resultado);
    } catch (err) {
      const mensaje =
        err instanceof ApiError &&
        typeof err.body === 'object' &&
        err.body !== null &&
        'detail' in err.body
          ? String((err.body as { detail: unknown }).detail)
          : 'Error de red al procesar tu solicitud.';
      setError(mensaje);
      setConfirmando(false);
    } finally {
      setSubmitting(false);
    }
  }

  const tituloPorTipo: Record<string, string> = {
    propietario_residente: 'Dejé el barrio (vendí o me mudé)',
    arrendatario: 'Dejé el barrio (termina mi arriendo)',
    comodatario: 'Dejé el barrio',
  };

  return (
    <Modal
      title={tituloPorTipo[tipoTenencia] ?? 'Dejé el barrio'}
      onClose={onCerrar}
      footer={
        <>
          <Button
            variant="ghost"
            type="button"
            onClick={onCerrar}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="form-deje-barrio"
            disabled={submitting}
          >
            {submitting
              ? 'Procesando…'
              : confirmando
                ? 'Sí, confirmo'
                : 'Continuar'}
          </Button>
        </>
      }
    >
      <form
        id="form-deje-barrio"
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        {!confirmando ? (
          <>
            <p className="text-sm text-text-secondary">
              {esTransicion ? (
                <>
                  Vas a marcar la Casa{' '}
                  <span className="text-text-primary font-medium">
                    {loteIdentificador}
                  </span>{' '}
                  como arrendada (sigues siendo el dueño y responsable de las
                  cuotas). Al confirmar:
                </>
              ) : (
                <>
                  Vas a cerrar tu vínculo con la Casa{' '}
                  <span className="text-text-primary font-medium">
                    {loteIdentificador}
                  </span>
                  . Al confirmar:
                </>
              )}
            </p>
            <ul className="text-xs text-text-muted space-y-1 list-disc list-inside">
              <li>Tus residentes registrados se inactivan.</li>
              <li>Tus vehículos se dan de baja del registro.</li>
              <li>
                Los controles remotos y llaveros físicos quedan en la
                bolsa de la casa (no los devolverás al barrio).
              </li>
              <li>Solicitudes de recursos pendientes se cancelan.</li>
              <li>
                La comisión recibe aviso para bajar celulares de
                citofonía y acceso WiFi.
              </li>
            </ul>
            <label className="text-sm space-y-1 block">
              <span className="text-text-secondary">
                Fecha de salida{' '}
                <span className="text-state-danger">*</span>
              </span>
              <InputFecha
                value={fecha}
                onChange={setFecha}
                max={fechaHoy}
                requerido
                className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
              />
            </label>
            <label className="text-sm space-y-1 block">
              <span className="text-text-secondary">
                Motivo <span className="text-state-danger">*</span>
              </span>
              <select
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                required
                className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
              >
                {motivos.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm space-y-1 block">
              <span className="text-text-secondary">
                Notas <span className="text-text-muted">(opcional)</span>
              </span>
              <textarea
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Ej: el comprador recibirá las llaves el 15 de junio."
                className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
              />
            </label>
          </>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-text-primary">
              {esTransicion ? (
                <>
                  ¿Confirmas que la Casa{' '}
                  <span className="font-medium">{loteIdentificador}</span>{' '}
                  pasa a estar arrendada y dejas de gestionar su hogar?
                </>
              ) : (
                <>
                  ¿Confirmas que dejas tu vínculo con la Casa{' '}
                  <span className="font-medium">{loteIdentificador}</span>{' '}
                  con fecha <span className="font-medium">{fecha}</span>?
                </>
              )}
            </p>
            <p className="text-xs text-text-muted">
              Esta acción NO es reversible desde tu cuenta. Si te
              equivocas, contacta a la comisión.
            </p>
          </div>
        )}
        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
